import React, { useState, useEffect } from "react";
import { MdClose, MdOutlineSettings } from "react-icons/md";
import { FaPaperPlane, FaSave } from "react-icons/fa";
import { MdOutlineAddLink } from "react-icons/md";
import { IoIosSave } from "react-icons/io";
import InputField from "../../common/InputField";
import Model from "../../common/Model";
import EntityAddModal from "../../common/EntityAddModal";

const MaterialInwardForm = ({
  formData: initialData,
  isEditing,
  isSaving,
  onSave,
  onCancel,
  isSupplierData,
  isRawMaterialData,
  isUnitData,
  handleInputChange,
  onEntityAddSuccess,
}) => {
  const [modalType, setModalType] = useState(null);
  const [formFields, setFormFields] = useState(initialData);

  useEffect(() => {
    setFormFields(initialData);
  }, [initialData]);

  const handleChange = (name, value) => {
    // Mirroring original handleInputChange logic
    handleInputChange({ target: { name, value } });
    setFormFields((prev) => ({ ...prev, [name]: value }));
  };

  const generateSupplierInitials = (name) => {
    if (!name || !name.trim()) return "SUPP";
    const initials = name
      .split(" ")
      .map((word) => word[0] || "")
      .join("")
      .toUpperCase()
      .slice(0, 4);
    return initials || "SUPP";
  };

  const formatDateForSku = (isoDate) => {
    try {
      const d = new Date(isoDate);
      const day = d.getDate().toString().padStart(2, "0");
      const month = (d.getMonth() + 1).toString().padStart(2, "0");
      return `${day}${month}`;
    } catch {
      return "XXXX";
    }
  };

  const formatInvoiceForSku = (invoiceNo) => {
    if (!invoiceNo || !invoiceNo.trim()) return "";
    const match = invoiceNo.replace(/[^0-9]/g, "");
    return match ? `-${match}` : "";
  };

  const generateScrapSku = (supplierName, dateReceivedIso, invoiceNo) => {
    const initials = generateSupplierInitials(supplierName);
    const datePart = formatDateForSku(dateReceivedIso);
    const invoicePart = formatInvoiceForSku(invoiceNo);
    return `${initials}${datePart}${invoicePart}`;
  };

  useEffect(() => {
    // Only generate SKU automatically if we're not editing or if we want to refresh it
    if (formFields.supplier_Id && formFields.inward_Date) {
      const supplierObj = isSupplierData.find(
        (s) => String(s.supplier_Id) === String(formFields.supplier_Id),
      );

      if (supplierObj) {
        const sku = generateScrapSku(
          supplierObj.supplier_Name,
          formFields.inward_Date,
          formFields.invoice_No,
        );
        
        // Update both local state and parent state
        setFormFields((prev) => ({
          ...prev,
          skU_No: sku,
        }));
        handleInputChange({ target: { name: "skU_No", value: sku } });
      }
    }
  }, [formFields.supplier_Id, formFields.inward_Date, formFields.invoice_No, isSupplierData]);

  const handleFileChange = (e) => {
    handleInputChange(e);
  };

  const handleSubmit = (e, resetOnly = false) => {
    if (e) e.preventDefault();
    onSave({ resetOnly });
  };

  return (
    <div className="flex flex-col h-full bg-white overflow-hidden font-plus animate-in fade-in duration-500">
      {/* Form Header */}
      <div className="bg-[#3F80F7] text-white px-8 py-4 flex justify-between items-center shrink-0">
        <div className="flex items-center gap-4">
          <MdOutlineSettings className="text-white text-2xl" />
          <div className="flex flex-col">
            <h2 className="text-[12px] font-black tracking-[3px] uppercase">
              {isEditing ? "Edit Material Inward" : "Add Material Inward"}
            </h2>
          </div>
        </div>
        <button
          type="button"
          onClick={onCancel}
          className="group p-2 hover:bg-white/10 transition-all border border-transparent hover:border-white/20"
        >
          <MdClose size={24} className="text-white group-hover:rotate-90 transition-transform duration-300" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-8 py-8 custom-scrollbar">
        <form className="space-y-12">
          {/* Section 1: Basic Information */}
          <section className="bg-white p-8 border border-gray-100  relative overflow-hidden group">
            <div className="flex items-center gap-4 mb-8 border-b border-gray-100 pb-4">
              <span className="w-1.5 h-4 bg-[#3F80F7]"></span>
              <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-[2.5px]">
                Basic Information
              </h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <InputField
                label="Inward Date"
                type="date"
                name="inward_Date"
                value={formFields.inward_Date}
                onChange={(val) => handleChange("inward_Date", val)}
                required
              />
              <InputField
                label="SKU No"
                name="skU_No"
                value={formFields.skU_No}
                onChange={(val) => handleChange("skU_No", val)}
                placeholder="AUTO-GENERATED"
                className="font-mono font-bold"
              />
              <InputField
                label="Invoice No"
                name="invoice_No"
                value={formFields.invoice_No}
                onChange={(val) => handleChange("invoice_No", val)}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
              <InputField
                label="Supplier"
                type="selectWithAdd"
                name="supplier_Id"
                value={formFields.supplier_Id}
                onChange={(selected) => handleChange("supplier_Id", selected?.value)}
                options={isSupplierData?.map((s) => ({
                  value: s.supplier_Id,
                  label: s.supplier_Name,
                }))}
                placeholder="Select Supplier"
                onAddNewClick={() => setModalType("supplier")}
                required
              />
              <InputField
                label="Raw Material"
                type="selectWithAdd"
                name="material_Id"
                value={formFields.material_Id}
                onChange={(selected) => handleChange("material_Id", selected?.value)}
                options={isRawMaterialData?.map((s) => ({
                  value: s.id,
                  label: s.name,
                }))}
                placeholder="Select Material"
                onAddNewClick={() => setModalType("rawMaterial")}
                required
              />
            </div>
          </section>

          {/* Section 2: Material Details & Weight */}
          <section className="bg-white p-8 border border-gray-100  relative overflow-hidden group">
            <div className="flex items-center gap-4 mb-8 border-b border-gray-100 pb-4">
              <span className="w-1.5 h-4 bg-[#3F80F7]"></span>
              <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-[2.5px]">
                Material Details & Weight
              </h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <InputField
                label="Quantity"
                type="number"
                name="quantity"
                value={formFields.quantity}
                onChange={(val) => handleChange("quantity", val)}
                required
              />
              <InputField
                label="Unit"
                type="selectWithAdd"
                name="unit_Id"
                value={formFields.unit_Id}
                onChange={(selected) => handleChange("unit_Id", selected?.value)}
                options={isUnitData?.map((s) => ({
                  value: s.unit_Id,
                  label: s.unit_Name,
                }))}
                placeholder="Select Unit"
                onAddNewClick={() => setModalType("unit")}
                required
              />
              <InputField
                label="Rate"
                type="number"
                name="rate"
                value={formFields.rate}
                onChange={(val) => handleChange("rate", val)}
                required
              />
              <InputField
                label="Grade"
                name="grade"
                value={formFields.grade}
                onChange={(val) => handleChange("grade", val)}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
              <InputField
                label="Slip 1 Weight"
                name="weight_Sleep1"
                value={formFields.weight_Sleep1}
                onChange={(val) => handleChange("weight_Sleep1", val)}
              />
              <InputField
                label="Slip 2 Weight"
                name="weight_Sleep2"
                value={formFields.weight_Sleep2}
                onChange={(val) => handleChange("weight_Sleep2", val)}
              />
            </div>
          </section>

          {/* Section 3: Logistics & Additional Info */}
          <section className="bg-white p-8 border border-gray-100  relative overflow-hidden group">
            <div className="flex items-center gap-4 mb-8 border-b border-gray-100 pb-4">
              <span className="w-1.5 h-4 bg-[#3F80F7]"></span>
              <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-[2.5px]">
                Logistics & Additional Info
              </h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <InputField
                label="Vehicle No"
                name="vehicle_No"
                value={formFields.vehicle_No}
                onChange={(val) => handleChange("vehicle_No", val)}
              />
              <InputField
                label="Batch No"
                name="batch_No"
                value={formFields.batch_No}
                onChange={(val) => handleChange("batch_No", val)}
              />
              <InputField
                label="Origin"
                name="origin"
                value={formFields.origin}
                onChange={(val) => handleChange("origin", val)}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mt-6">
              <div className="md:col-span-8">
                <InputField
                  label="Remark"
                  type="textarea"
                  name="notes"
                  value={formFields.notes}
                  onChange={(val) => handleChange("notes", val)}
                  rows={4}
                />
              </div>
              <div className="md:col-span-4">
                <InputField
                  label="Image"
                  type="file"
                  name="image_Upload"
                  onChange={handleFileChange}
                  className="h-full"
                />
              </div>
            </div>
          </section>
        </form>
      </div>

      {/* Action Footer */}
      <div className="flex flex-col md:flex-row justify-between items-center py-6 border-t-2 border-gray-100 bg-white px-8 -mx-0 mt-0 gap-8 -4px_20px_-5px_rgba(0,0,0,0.05)]">
        <button
          type="button"
          onClick={onCancel}
          className="px-10 py-3 border-2 border-gray-100 text-gray-400 font-black text-[10px] uppercase tracking-[3px] hover:bg-gray-50 hover:text-gray-600 transition-all font-plus"
          disabled={isSaving}
        >
          Discard
        </button>

        <div className="flex items-center gap-4 w-full md:w-auto">
          {!isEditing && (
            <button
              type="button"
              onClick={(e) => handleSubmit(e, true)}
              className="flex-1 md:flex-none px-8 py-3 border-2 border-[#3F80F7] text-[#3F80F7] font-black text-[10px] uppercase tracking-[3px] hover:bg-blue-50 transition-all flex items-center justify-center gap-3"
              disabled={isSaving}
            >
              <MdOutlineAddLink size={18} /> Save & Add New
            </button>
          )}

          <button
            type="submit"
            onClick={(e) => handleSubmit(e, false)}
            className="flex-1 md:flex-none bg-[#3F80F7] text-white font-black text-[11px] uppercase tracking-[3px] px-16 py-3 hover:bg-[#2c5ebc] transition-all flex items-center justify-center gap-4  active:scale-95 disabled:opacity-50 font-plus"
            disabled={isSaving}
          >
            {isSaving ? "Saving..." : isEditing ? "Save Change" : "Submit"}
            <FaPaperPlane size={14} className={isSaving ? "animate-pulse" : ""} />
          </button>
        </div>
      </div>

      {/* Internal Modals for Add New */}
      <Model
        isOpen={!!modalType}
        onClose={() => setModalType(null)}
        name={`Add ${modalType}`}
      >
        <EntityAddModal
          type={modalType}
          onClose={(newOption) => {
            setModalType(null);
            if (newOption && onEntityAddSuccess) {
              onEntityAddSuccess(modalType, newOption);
            }
          }}
        />
      </Model>
    </div>
  );
};

export default MaterialInwardForm;
